import { Head, Link } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"

type Breakdown = {
  question: string
  prompt: string
  answer_key: string
  answer_label: string
  points: number
  commentary: string
}

type Scorecard = {
  id: number
  token: string
  email: string
  name: string | null
  podcast_name: string | null
  podcast_url: string | null
  grade: string
  score: number | null
  answers: Record<string, string>
  breakdown: Breakdown[]
  commentary: string | null
  kit_synced: boolean
  kit_subscriber_id: string | null
  kit_synced_at: string | null
  kit_sync_error: string | null
  has_error: boolean
  referrer: string | null
  utm_source: string | null
  utm_medium: string | null
  utm_campaign: string | null
  created_at: string
}

export default function AdminPodcastScorecardsShow({ scorecard }: { scorecard: Scorecard }) {
  return (
    <>
      <Head title={`Scorecard — ${scorecard.email}`} />
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="text-sm text-ink-muted">
            <Link href="/admin/podcast_scorecards" className="underline-offset-2 hover:underline">
              ← Scorecards
            </Link>
          </div>
          <h1 className="mt-3">{scorecard.email}</h1>
          <p className="mt-1 text-ink-body">
            {[scorecard.name, scorecard.podcast_name].filter(Boolean).join(" · ") || "—"}
          </p>
        </div>

        <div className="mt-6 grid gap-6 sm:grid-cols-2">
          {/* Grade card */}
          <div className="rounded-xl border border-hairline bg-surface p-6">
            <div className="text-sm text-ink-muted">Grade</div>
            <div className="mt-2 flex items-baseline gap-3">
              <div className="text-5xl font-bold text-ink-display">{scorecard.grade}</div>
              {scorecard.score !== null && (
                <div className="text-ink-muted">{scorecard.score}/100</div>
              )}
            </div>
            <p className="mt-3 text-sm text-ink-muted">
              Taken {new Date(scorecard.created_at).toLocaleString()}
            </p>
            {scorecard.podcast_url && (
              <p className="mt-2 text-sm">
                <a href={scorecard.podcast_url} target="_blank" rel="noopener noreferrer" className="text-ink-display underline-offset-2 hover:underline">
                  {scorecard.podcast_url}
                </a>
              </p>
            )}
          </div>

          {/* Kit sync card */}
          <div className="rounded-xl border border-hairline bg-surface p-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-ink-muted">Kit sync</div>
              {scorecard.has_error ? (
                <Badge tone="accent">error</Badge>
              ) : scorecard.kit_synced ? (
                <Badge tone="signal">synced</Badge>
              ) : (
                <Badge tone="muted">pending</Badge>
              )}
            </div>
            {scorecard.kit_subscriber_id && (
              <p className="mt-3 text-sm">
                Subscriber id: <code className="text-ink-display">{scorecard.kit_subscriber_id}</code>
              </p>
            )}
            {scorecard.kit_synced_at && (
              <p className="mt-1 text-sm text-ink-muted">
                Synced {new Date(scorecard.kit_synced_at).toLocaleString()}
              </p>
            )}
            {scorecard.kit_sync_error && (
              <p className="mt-3 text-sm text-danger-display">{scorecard.kit_sync_error}</p>
            )}
          </div>
        </div>

        {/* Source attribution */}
        {(scorecard.utm_source || scorecard.referrer) && (
          <div className="mt-6 rounded-xl border border-hairline bg-surface p-6">
            <div className="text-sm text-ink-muted">Source</div>
            <p className="mt-2 text-sm text-ink-body">
              {[scorecard.utm_source, scorecard.utm_medium, scorecard.utm_campaign, scorecard.referrer].filter(Boolean).join(" · ")}
            </p>
          </div>
        )}

        {/* Breakdown */}
        <div className="mt-8">
          <h2>Answers</h2>
          <div className="mt-4 space-y-3">
            {scorecard.breakdown.map((b) => (
              <div key={b.question} className="rounded-xl border border-hairline bg-page p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm text-ink-muted">{b.question}</div>
                    <h3 className="mt-1">{b.prompt}</h3>
                    <p className="mt-2 text-ink-body">
                      → {b.answer_label}
                    </p>
                  </div>
                  <Badge tone={b.points >= 15 ? "signal" : b.points >= 5 ? "muted" : "accent"}>
                    {b.points} pt{b.points === 1 ? "" : "s"}
                  </Badge>
                </div>
                <p className="mt-3 text-sm text-ink-muted">{b.commentary}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 text-sm text-ink-muted">
          <Link
            href={`/podcast-scorecard/${scorecard.token}`}
            target="_blank"
            className="underline-offset-2 hover:underline"
          >
            View the public report ↗
          </Link>
        </div>
      </AdminShell>
    </>
  )
}
